import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

gsap.registerPlugin(ScrollTrigger);

type UseCareJourneyOptions = {
  chapterCount: number;
  onProgress?: (progress: number) => void;
};

export function useCareJourney({ chapterCount, onProgress }: UseCareJourneyOptions) {
  const sectionRef = useRef<HTMLElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const [activeChapter, setActiveChapter] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const section = sectionRef.current;
    const pin = pinRef.current;
    const copy = copyRef.current;

    if (!section || !pin || !copy) {
      return;
    }

    const chapterElements = copy.querySelectorAll('[data-chapter]');

    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: () => `+=${window.innerHeight * (chapterCount - 0.5)}`,
        pin: pin,
        scrub: 0.6,
        anticipatePin: 1,
        onUpdate: (self) => {
          const progress = self.progress;
          const index = Math.min(chapterCount - 1, Math.floor(progress * chapterCount));
          setActiveChapter(index);
          onProgress?.(progress);
        },
      },
    });

    chapterElements.forEach((element, index) => {
      if (index === 0) {
        return;
      }

      timeline.fromTo(
        element,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 1 },
        index,
      );
    });

    return () => {
      timeline.scrollTrigger?.kill();
      timeline.kill();
    };
  }, [chapterCount, onProgress, prefersReducedMotion]);

  return {
    sectionRef,
    pinRef,
    copyRef,
    activeChapter: prefersReducedMotion ? 0 : activeChapter,
    prefersReducedMotion,
  };
}
